<template>
  <div
    class="btn-square flex items-center justify-center cursor-grab text-icon-default bg-background-strongest hover:bg-icon-bg group"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
  >
    <em
      class="custom-icons h-5 w-5 flex-shrink-0 group-hover:bg-icon-hover"
      :style="[getCustomIconStyle(iconPath('drag'))]"
    />
  </div>
</template>

<script>
import { NodeSelection } from '@tiptap/pm/state';
import EventBus from '@/utilities/eventBus';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { getRootStyle } from '@/utilities/rootStyles';
import useIsBrowser from '@/utilities/composables/useIsBrowser';

const blockPositionFromCoords = (block, view) => {
  if (block && block.nodeType === 1) {
    const docView = view.docView;
    const desc = docView.nearestDesc(block, true);
    if (!desc || desc === docView) {
      return null;
    }
    return desc.posBefore;
  }
  return null;
};

export default {
  name: 'DragHandle',

  props: {
    element: {
      type: HTMLElement,
      required: true,
    },

    editorId: {
      type: String,
      required: true,
    },
  },

  setup() {
    const { getCustomIconStyle, iconPath } = useCustomIcon();
    const { isSafari } = useIsBrowser();
    return {
      getCustomIconStyle,
      iconPath,
      isSafari,
    };
  },

  data() {
    return {
      timeout: null,
    };
  },

  computed: {
    editor() {
      return this.$store.getters.editor(this.editorId);
    },
  },

  unmounted() {
    clearTimeout(this.timeout);
  },

  methods: {
    removeCommentInputAndHiglight() {
      this.editor.commands.removePreCommentHighlightSelection();
      this.$store.dispatch('commentPopupVisible', false);
      EventBus.$emit('displayInlineCommentInput', {
        rect: null,
        text: '',
        editorId: this.editorId,
      });
    },

    setDragImage(event, ghost, useOffsetX) {
      const slice = this.editor.state.selection.content();
      event.dataTransfer.clearData();
      event.dataTransfer.setData('text/html', this.element.outerHTML);

      const offsetX =
        this.isSafari && useOffsetX ? parseInt(getRootStyle('--page-elements-side-padding')) : 0;
      event.dataTransfer.setDragImage(ghost, offsetX, 0);
      event.dataTransfer.effectAllowed = 'move';

      // Indicate we are dragging
      this.editor.view.dragging = { slice, move: true };
      this.editor.storage.MouseOver.dragging = true;
    },

    createGhostElement(element, elementWidth, isTable, isTableFullScreen) {
      const ghost = element.cloneNode(true);
      ghost.classList.add('editor-drag-ghost-element');

      if (!isTable) {
        ghost.style.width = `${elementWidth - parseInt(getRootStyle('--page-elements-side-padding')) * 2}px`;
      } else if (isTableFullScreen) {
        const tableNode = element.querySelector('table');
        const tableWidth = tableNode.getBoundingClientRect().width;
        ghost.style.width = `${tableWidth}px`;
      } else {
        ghost.style.width = `${elementWidth}px`;
      }

      return ghost;
    },

    onDragStart(event) {
      const pos = blockPositionFromCoords(this.element, this.editor.view);
      if (pos == null) return;

      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.editor.commands.hideBlockMenu();
      });

      // if there is highlighted text for comment, remove it and close the comment input
      if (this.editor?.storage?.precommenthighlight?.highlightedId) {
        this.removeCommentInputAndHiglight();
      }

      this.editor.view.dispatch(
        this.editor.view.state.tr.setSelection(
          NodeSelection.create(this.editor.view.state.doc, pos)
        )
      );

      // Handle special elements (images and iframes)
      if (
        (this.element.tagName === 'DIV' && this.element.classList.contains('st-image-wrap')) ||
        this.element.tagName === 'IFRAME'
      ) {
        this.setDragImage(event, this.element);
        return;
      }

      // Create ghost element
      const elementWidth = this.element.getBoundingClientRect().width;
      const isTable = this.element.classList.contains('tableWrapper');
      const isTableFullScreen =
        isTable && this.element.getAttribute('data-content-width') === 'full';
      const ghost = this.createGhostElement(this.element, elementWidth, isTable, isTableFullScreen);

      // Append ghost to DOM
      const stEditorDiv = document.createElement('div');
      stEditorDiv.classList.add('st-editor');
      stEditorDiv.id = 'st-editor-drag-ghost';
      const stEditorContent = document.createElement('div');
      stEditorContent.classList.add('st-editor-content', 'editor-drag-ghost');
      stEditorContent.appendChild(ghost);
      stEditorDiv.appendChild(stEditorContent);
      document.body.appendChild(stEditorDiv);

      this.setDragImage(event, this.isSafari ? this.element : ghost, !isTable);
    },
    onDragEnd() {
      const ghost = document.getElementById('st-editor-drag-ghost');
      if (ghost && ghost.parentNode) {
        ghost.parentNode.removeChild(ghost);
      }
      // Reset dragging state
      if (this.editor?.storage?.MouseOver) {
        this.editor.storage.MouseOver.dragging = false;
      }
    },
  },
};
</script>
